import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { TermService } from '../term-service';
import { Term, Specification, Figure } from '../../services/impls/specification';
import { Message } from '@patsnap/tahiti-core';
import { I18nService } from '@telecom/shared/services/i18n';

@injectable()
export class TermServiceImpl implements TermService {
    constructor(
        @inject(Specification)
        private readonly _specification: Specification,
        @inject(Message)
        private readonly _message: Message,
        @inject(I18nService)
        private readonly _i18nService: I18nService,
    ) {}

    setNumber(term: Term, number: string): void {
        if (this._checkExistNumber(number, term)) {
            const message = this._i18nService.t('aiSpecification.term.numberAlreadyExists');
            this._message.warning({ message });
            return;
        }
        term.setNumber(number);
        // @ts-ignore
        const terms: Term[] = this._specification._terms;
        if (!terms.some(t => t.getId() === term.getId())) return;
        terms.sort((a, b) => this._compareNumbers(a.number, b.number));
        // @ts-ignore
        this._specification._terms = terms;
        // @ts-ignore
        this._specification._onTermsChangeEmitter.fire(this._specification.getAllTerms());
    }

    private _checkExistNumber(number: string, term: Term): boolean {
        return this._specification.getAllTerms().some(t => t.number === number && t.getId() !== term.getId());
    }

    /**
     * @description 比较两个数字字符串，按自然数排序
     *  arrange: 221, 220, 202, 200, 111, 110, 101, 100, 22B, 22, 11, 2, 1
     *  expect: 1, 11, 100, 101, 110, 111, 2, 22, 22B, 200, 202, 220, 221
     * @param a
     * @param b
     */
    private _compareNumbers(a: string, b: string): number {
        // 1. 空字符串优先处理
        const emptyResult = this._isEmptyLast(a, b);
        if (emptyResult !== null) return emptyResult;
        // 2. 解析
        const pa = this._parseNumberParts(a);
        const pb = this._parseNumberParts(b);
        // 3. 首数字
        const firstDigitResult = this._compareFirstDigit(pa, pb);
        if (firstDigitResult !== null) return firstDigitResult;
        // 4. 完整数字
        const fullNumberResult = this._compareFullNumber(pa, pb);
        if (fullNumberResult !== null) return fullNumberResult;
        // 5. 后缀
        const suffixResult = this._compareSuffix(pa, pb);
        if (suffixResult !== null) return suffixResult;
        // 6. 长度
        const lengthResult = this._compareLength(pa, pb);
        if (lengthResult !== null) return lengthResult;
        // 7. 兜底
        return this._compareFallback(a, b);
    }

    // 判断空字符串排序逻辑
    // eslint-disable-next-line complexity
    private _isEmptyLast(a: string, b: string): number | null {
        if (a === '' && b !== '') return 1;
        if (b === '' && a !== '') return -1;
        if (a === '' && b === '') return 0;
        return null;
    }

    // 解析字符串，提取首数字、完整数字、后缀
    private _parseNumberParts(str: string) {
        const match = str.match(/^(\d+)([A-Za-z]*)$/);
        return match
            ? { firstDigit: Number(match[1][0]), num: Number(match[1]), suffix: match[2] || '', raw: str }
            : { firstDigit: NaN, num: NaN, suffix: '', raw: str };
    }

    // 比较首数字
    private _compareFirstDigit(pa: any, pb: any): number | null {
        if (!isNaN(pa.firstDigit) && !isNaN(pb.firstDigit) && pa.firstDigit !== pb.firstDigit) {
            return pa.firstDigit - pb.firstDigit;
        }
        return null;
    }

    // 比较完整数字
    private _compareFullNumber(pa: any, pb: any): number | null {
        if (!isNaN(pa.num) && !isNaN(pb.num) && pa.num !== pb.num) {
            return pa.num - pb.num;
        }
        return null;
    }

    // 比较字母后缀
    private _compareSuffix(pa: any, pb: any): number | null {
        if (pa.suffix !== pb.suffix) {
            return pa.suffix.localeCompare(pb.suffix);
        }
        return null;
    }

    // 比较原始字符串长度
    private _compareLength(pa: any, pb: any): number | null {
        if (pa.raw.length !== pb.raw.length) {
            return pa.raw.length - pb.raw.length;
        }
        return null;
    }

    // 兜底比较
    private _compareFallback(a: string, b: string): number {
        return a.localeCompare(b);
    }

    setText(term: Term, text: string): void {
        term.setText(text);
        // @ts-ignore
        this._specification._onTermsChangeEmitter.fire(this._specification.getAllTerms());
    }

    addTerm(term: Term, head: boolean = false): void {
        const terms: Term[] = this._specification.getAllTerms();
        if (term.id === '') {
            const maxId = terms.reduce((max, t) => Math.max(max, Number(t.id)), 0);
            term.setId(String(maxId + 1));
        }
        this._specification.addTerm(term, head);
    }

    getFiguresByTermId(termId: string): Figure[] {
        const allFigures = this._specification.getAllFigures();
        return allFigures.filter(figure =>
            figure.annotations.some(annotation => annotation.getTermId() === termId),
        );
    }
}

